/**
 * When the open channel's newest message may be reported as read
 * (`POST /api/channels/:id/read`). Throttled, not debounced: the first new
 * message starts the timer and later ones only move the pending id, so a
 * channel busier than the delay still advances its pointer.
 *
 * Only what was on screen is reported: a message noted while the page is
 * hidden is not pending at all (the caller notes the newest one again when the
 * page comes back). What was seen while visible is sent at once when the
 * channel is left or the page is hidden, so a quick switch or minimizing never
 * drops it.
 */
export interface ReadMarkScheduler {
  /** The open channel's newest message changed (or the page became visible). */
  note(channelId: string, messageId: string, delayMs: number): void;
  /** Send what is pending now (channel left, page hidden). */
  flush(): void;
  /** Drop everything without sending (teardown). */
  dispose(): void;
}

export function createReadMarkScheduler(
  send: (channelId: string, messageId: string) => void,
  isVisible: () => boolean,
): ReadMarkScheduler {
  let pending: { channelId: string; messageId: string } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const stopTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const flush = () => {
    stopTimer();
    if (!pending) return;
    const { channelId, messageId } = pending;
    pending = null;
    send(channelId, messageId);
  };

  return {
    note(channelId, messageId, delayMs) {
      if (pending && pending.channelId !== channelId) flush();
      if (!isVisible()) return;
      pending = { channelId, messageId };
      if (timer === null) timer = setTimeout(flush, delayMs);
    },
    flush,
    dispose() {
      stopTimer();
      pending = null;
    },
  };
}
